import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Globe } from 'lucide-react';

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
];

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (langCode) => {
    const currentPath = location.pathname;
    let newPath = currentPath;

    // Remove current language prefix if exists
    const langPattern = /^\/(en|fr|es)(\/|$)/;
    if (langPattern.test(currentPath)) {
      newPath = currentPath.replace(langPattern, '/');
    }

    // Add new language prefix (except for English which is default)
    if (langCode !== 'en') {
      newPath = `/${langCode}${newPath}`;
    }

    // Ensure path doesn't have double slashes
    newPath = newPath.replace(/\/+/g, '/');

    // Change language
    i18n.changeLanguage(langCode);
    
    // Navigate to new path
    navigate(newPath);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-grey-700/50 hover:bg-gray-700 transition-colors duration-200 border border-gray-700"
        aria-label="Select language"
      >
        <Globe className="w-4 h-4 text-kitco-gold" />
        <span className="text-white font-medium text-sm">{currentLanguage.code.toUpperCase()}</span>
        <svg
          className={`w-4 h-4 text-white transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-grey-50 rounded-lg shadow-xl border border-gray-200 py-1 z-50 overflow-hidden">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`w-full flex items-center space-x-3 px-4 py-3 hover:bg-amber-50 transition-colors duration-150 ${
                currentLanguage.code === lang.code ? 'bg-amber-50 border-l-4 border-kitco-gold' : ''
              }`}
            >
              <span className="text-2xl">{lang.flag}</span>
              <span className={`text-kitco-navy font-medium flex-1 text-left ${
                currentLanguage.code === lang.code ? 'text-kitco-gold-hover' : ''
              }`}>
                {lang.name}
              </span>
              {currentLanguage.code === lang.code && (
                <svg className="w-5 h-5 text-kitco-gold-hover" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
